class ThemeAccordionGroup extends HTMLElement {
  #controller = null;
  #pendingScrollAccordion = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller.signal;

    requestAnimationFrame(() => {
      this.#normalizeInitialOpenState();
    });

    this.addEventListener(
      'transitionend',
      (event) => {
        this.#onTransitionEnd(event);
      },
      { signal, capture: true },
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#pendingScrollAccordion = null;
  }

  handleAccordionToggle(accordion) {
    if (!this.#isAccordion(accordion)) {
      return;
    }

    if (accordion.isOpen) {
      accordion.close({ animate: true });
      return;
    }

    this.handleAccordionOpen(accordion, { animate: true, scroll: true });
  }

  handleAccordionOpen(accordion, options = {}) {
    if (!this.#isAccordion(accordion)) {
      return;
    }

    const { animate = true, scroll = false } = options;

    if (accordion.isOpen) {
      return;
    }

    const isExclusive = this.dataset.exclusive !== 'false';

    if (isExclusive) {
      const hadOpenSibling = this.#getAccordions().some((item) => item !== accordion && item.isOpen);

      if (scroll && hadOpenSibling) {
        this.#pendingScrollAccordion = accordion;
      }

      this.#getAccordions().forEach((item) => {
        if (item !== accordion && item.isOpen) {
          item.close({ animate: false });
        }
      });
    }

    accordion.open({ animate, fromGroup: true });
  }

  #isAccordion(accordion) {
    return accordion?.matches?.('theme-accordion') === true;
  }

  #getAccordions() {
    return [...this.querySelectorAll('theme-accordion')];
  }

  #normalizeInitialOpenState() {
    const accordions = this.#getAccordions();
    let foundOpen = false;

    accordions.forEach((accordion) => {
      const wantsOpen = accordion.hasAttribute('data-open') || accordion.isOpen;

      if (!wantsOpen) {
        return;
      }

      if (!foundOpen) {
        foundOpen = true;

        if (!accordion.isOpen) {
          accordion.open({ animate: false, fromGroup: true });
        }

        return;
      }

      accordion.close({ animate: false });
    });
  }

  #onTransitionEnd(event) {
    if (event.propertyName !== 'grid-template-rows') {
      return;
    }

    const panel = event.target;

    if (!panel?.matches?.('[data-accordion-panel]')) {
      return;
    }

    const accordion = panel.closest('theme-accordion');

    if (!accordion || !this.contains(accordion)) {
      return;
    }

    if (accordion !== this.#pendingScrollAccordion || !accordion.isOpen) {
      return;
    }

    this.#scrollToAccordion(accordion);
    this.#pendingScrollAccordion = null;
  }

  #scrollToAccordion(accordion) {
    const trigger = accordion.querySelector('[data-accordion-trigger]');

    if (!trigger) {
      return;
    }

    trigger.scrollIntoView({
      block: 'start',
      behavior: 'auto',
    });
  }
}

if (!customElements.get('theme-accordion-group')) {
  customElements.define('theme-accordion-group', ThemeAccordionGroup);
}
